import { useTranslation } from 'react-i18next'
import React, { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import { TransportEquipmentType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankTransportEquipment } from 'features/declaration/models/blankItems'
import useDeclarationFieldRequirements from '../../form/components/useDeclarationFieldRequirements'
import FormCollapsibleColumn from '../../../../components/p5/FormCollapsibleColumn'
import FormFieldArray from '../../../../components/p5/FormFieldArray'
import FormField from '../../../../components/p5/FormField'
import Input from '../../../../components/p5/Input'
import FormSelect from '../../../../components/p5/FormSelect'
import Seals from './Seals'

const scope = 'transportEquipment'

function TransportEquipment() {
  const { t } = useTranslation()
  const { control } = useFormContext<ConsignmentItemFormValues>()

  const {
    softRemove,
    appendWithSequence,
    visibleFields,
  } = useSoftDeleteFieldArray<ConsignmentItemFormValues, TransportEquipmentType>({
    control,
    name: scope,
  })

  const [, containerSizeAndIdentificationOptions] = useCodelist('CONTAINER_SIZE_AND_TYPE_IDENTIFICATIONS')
  const [, containerPackedStatusOptions] = useCodelist('CONTAINER_PACKED_STATUSES')
  const [, containerSupplierTypeOptions] = useCodelist('CONTAINER_SUPPLIER_TYPES')

  const paths = useMemo(
    () => [
      scope,
      ...visibleFields.map((f) => `${scope}.${f.index}.containerIdentificationNumber`),
      ...visibleFields.map((f) => `${scope}.${f.index}.containerSizeAndType`),
      ...visibleFields.map((f) => `${scope}.${f.index}.containerSupplierType`),
    ],
    [visibleFields],
  )

  const fieldRequirements = useDeclarationFieldRequirements<ConsignmentItemFormValues>('consignmentItem', paths)
  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  const addNew = useCallback(() => appendWithSequence(blankTransportEquipment), [appendWithSequence])

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-transport-equipment-section"
        headingLabel={t('declaration.p5.transportEquipment')}
        fieldRule={fieldRequirements[scope]}
      >
        {visibleFields.map((fieldWithIndex) => {
          const equipmentItem = fieldWithIndex.field
          const innerItemScope = `transportEquipment.${fieldWithIndex.index}` as const

          return (
            <FormFieldArray
              key={`equipmentItem_${equipmentItem.id}`}
              titleKey="declaration.p5.sequenceNumber"
              sequenceNumber={equipmentItem.sequenceNumber + 1}
              removeItem={() => softRemove(fieldWithIndex.index)}
            >
              <div className="equipment-fields">
                <FormField
                  labelKey="declaration.p5.containerNumber"
                  fieldRule={fieldRequirements[`${innerItemScope}.containerIdentificationNumber`]}
                >
                  <Input
                    field={`${innerItemScope}.containerIdentificationNumber`}
                    labelKey="declaration.p5.containerNumber"
                    type="text"
                    placeholder={t('common.fieldPlaceholder')}
                    fieldRule={fieldRequirements[`${innerItemScope}.containerIdentificationNumber`]}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.containerSizeAndType"
                  fieldRule={fieldRequirements[`${innerItemScope}.containerSizeAndType`]}
                >
                  <FormSelect
                    field={`${innerItemScope}.containerSizeAndType`}
                    labelKey="declaration.p5.containerSizeAndType"
                    options={containerSizeAndIdentificationOptions}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.containerSizeAndType`]}
                  />
                </FormField>
                <FormField labelKey="declaration.p5.containerPackedStatus">
                  <FormSelect
                    field={`${innerItemScope}.packedStatus`}
                    labelKey="declaration.p5.containerPackedStatus"
                    options={containerPackedStatusOptions}
                    type="sync"
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.containerSupplierType"
                  fieldRule={fieldRequirements[`${innerItemScope}.containerSupplierType`]}
                >
                  <FormSelect
                    field={`${innerItemScope}.supplierType`}
                    labelKey="declaration.p5.containerSupplierType"
                    options={containerSupplierTypeOptions}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.containerSupplierType`]}
                  />
                </FormField>
              </div>
              <Seals transportEquipmentIndex={fieldWithIndex.index} />
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={addNew}
              disabled={isAddForbidden}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewContainer')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default TransportEquipment
